import React, { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

// Import particles.js - it will attach to window when loaded
let particlesJS;
if (typeof window !== 'undefined') {
  // Load the module - it will attach particlesJS to window
  require('particles.js');
  // Get it from window after loading
  particlesJS = window.particlesJS;
}

const ParticlesBackground = () => {
  const { resolvedTheme } = useTheme();
  const particlesRef = useRef(null);
  const particlesInstance = useRef(null);

  useEffect(() => {
    // Wait for DOM to be ready
    const initParticles = () => {
      const element = document.getElementById('particles-background');
      if (!element) {
        console.warn('Particles container element not found');
        return;
      }

      if (!particlesJS) {
        console.warn('particles.js is not loaded');
        return;
      }

      console.log('Initializing particles.js, type:', typeof particlesJS);

      // Destroy existing particles instance if it exists
      const pJSDom = window.particlesJS?.pJSDom;
      if (particlesInstance.current && pJSDom) {
        try {
          if (particlesInstance.current.pJS && particlesInstance.current.pJS.fn && particlesInstance.current.pJS.fn.vendors) {
            particlesInstance.current.pJS.fn.vendors.destroypJS();
          }
        } catch (e) {
          console.warn('Error destroying particles:', e);
        }
      }

    // Determine colors based on theme
    const isDark = resolvedTheme === 'dark';
    const particleColor = isDark ? '#FF8A65' : '#000000'; // Orange for dark, black for light
    const lineColor = isDark ? '#FF7043' : '#333333'; // Lighter orange for dark, dark gray for light

    // Particles configuration - optimized for performance
    const particlesConfig = {
      particles: {
        number: {
          value: 40, // Reduced from 80 for better performance
          density: {
            enable: true,
            value_area: 800
          }
        },
        color: {
          value: particleColor
        },
        shape: {
          type: 'circle',
          stroke: {
            width: 0,
            color: '#000000'
          }
        },
        opacity: {
          value: isDark ? 0.5 : 0.4,
          random: false,
          anim: {
            enable: false,
            speed: 1,
            opacity_min: 0.1,
            sync: false
          }
        },
        size: {
          value: 3,
          random: true,
          anim: {
            enable: false,
            speed: 5,
            size_min: 0.1,
            sync: false
          }
        },
        line_linked: {
          enable: true,
          distance: 100, // Reduced from 150 to reduce connections
          color: lineColor,
          opacity: isDark ? 0.3 : 0.2,
          width: 2
        },
        move: {
          enable: true,
          speed: 1.5, // Reduced from 2 for smoother performance
          direction: 'none',
          random: false,
          straight: false,
          out_mode: 'bounce',
          bounce: true,
          attract: {
            enable: true,
            rotateX: 600,
            rotateY: 1200
          }
        }
      },
      interactivity: {
        detect_on: 'canvas',
        events: {
          onhover: {
            enable: false,
            mode: 'repulse'
          },
          onclick: {
            enable: false,
            mode: 'push'
          },
          resize: true
        },
        modes: {
          grab: {
            distance: 200, // Reduced from 400
            line_linked: {
              opacity: 1
            }
          },
          bubble: {
            distance: 300, // Reduced from 400
            size: 40,
            duration: 2,
            opacity: 8,
            speed: 3
          },
          repulse: {
            distance: 150, // Reduced from 200 to affect fewer particles
            duration: 0.4
          },
          push: {
            particles_nb: 4 // Reduced from 4 to push fewer particles
          },
          remove: {
            particles_nb: 2
          }
        }
      },
      retina_detect: false // Disabled for better performance
    };

      // Initialize particles
      try {
        if (typeof particlesJS === 'function') {
          particlesJS('particles-background', particlesConfig);
          // Store reference to the particles instance
          setTimeout(() => {
            // Access pJSDom from window.particlesJS
            const pJSDom = window.particlesJS?.pJSDom;
            if (pJSDom && pJSDom.length > 0) {
              particlesInstance.current = pJSDom[pJSDom.length - 1];
            }
          }, 100);
        } else {
          console.warn('particlesJS is not a function. Type:', typeof particlesJS, 'Available:', !!window.particlesJS);
          // Try to load it again
          if (!window.particlesJS) {
            require('particles.js');
            particlesJS = window.particlesJS;
            if (typeof particlesJS === 'function') {
              particlesJS('particles-background', particlesConfig);
            }
          }
        }
      } catch (error) {
        console.warn('Error initializing particles:', error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initParticles, 100);

    // Cleanup function
    return () => {
      clearTimeout(timer);
      if (particlesInstance.current) {
        try {
          const pJSDom = window.particlesJS?.pJSDom;
          if (pJSDom) {
            // Filter out the current instance
            const filtered = pJSDom.filter(p => p.pJS !== particlesInstance.current);
            if (window.particlesJS) {
              window.particlesJS.pJSDom = filtered;
            }
          }
          if (particlesInstance.current.pJS && particlesInstance.current.pJS.fn && particlesInstance.current.pJS.fn.vendors) {
            particlesInstance.current.pJS.fn.vendors.destroypJS();
          }
        } catch (e) {
          console.warn('Error cleaning up particles:', e);
        }
      }
    };
  }, [resolvedTheme]);

  return (
    <div
      id="particles-background"
      ref={particlesRef}
      className="absolute inset-0 w-full h-full"
      style={{ zIndex: 0, pointerEvents: 'none' }}
    />
  );
};

export default ParticlesBackground;

